import { useEffect, useEffectEvent } from 'react';

import { safeLocalStorage } from '@/shared/services/helpers/DOM';

/** События, которые считаются действием пользователя. */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll'
] as const;

/** Как часто активность записывается в общее для вкладок хранилище. */
const ACTIVITY_WRITE_INTERVAL_MS = 5_000;

interface IOptions {
  /** Сколько бездействовать до `onIdle`. */
  timeoutMs: number;
  /** Вызывается один раз, когда пользователь бездействовал `timeoutMs`. */
  onIdle: () => void;
  /**
   * Ключ localStorage с временем последней активности: через него вкладки делят
   * активность — пока пользователь работает в одной вкладке, остальные не простаивают.
   */
  storageKey: string;
  /** Выключенный хук ничего не отслеживает. */
  enabled?: boolean;
}

/**
 * Отслеживает бездействие пользователя во всех вкладках сайта и вызывает
 * `onIdle`, когда с последнего действия прошло `timeoutMs`. Срок проверяется
 * и при возвращении на вкладку: таймеры спящей вкладки или ноутбука могут
 * опоздать, но время считается по часам, а не по таймеру.
 */
const useIdleTimeout = ({
  timeoutMs,
  onIdle,
  storageKey,
  enabled = true
}: IOptions): void => {
  const handleIdle = useEffectEvent(onIdle);

  useEffect(() => {
    if (!enabled) return;

    let lastActivity = Date.now();
    let lastWrite = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const markActivity = (): void => {
      lastActivity = Date.now();
      if (lastActivity - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return;
      lastWrite = lastActivity;
      safeLocalStorage.set(storageKey, String(lastActivity));
    };

    const check = (): void => {
      const sharedActivity = Number(safeLocalStorage.get(storageKey)) || 0;
      const deadline = Math.max(lastActivity, sharedActivity) + timeoutMs;
      const remaining = deadline - Date.now();

      clearTimeout(timer);
      if (remaining <= 0) {
        handleIdle();
        return;
      }
      timer = setTimeout(check, remaining);
    };

    const checkOnReturn = (): void => {
      if (document.visibilityState === 'visible') check();
    };

    markActivity();
    timer = setTimeout(check, timeoutMs);
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, {
        passive: true,
        capture: true
      })
    );
    document.addEventListener('visibilitychange', checkOnReturn);

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity, { capture: true })
      );
      document.removeEventListener('visibilitychange', checkOnReturn);
    };
  }, [enabled, timeoutMs, storageKey]);
};

export default useIdleTimeout;
